import * as React from "react";
import {useMemo, useState} from "react";
import {Box} from "@mui/material";
import {RequestFilter} from "@/api/data-contracts";
import {OrderFilter} from "@/model/order";
import {useTranslation} from "react-i18next";
import {withAuthWrapperComponent} from "@/app/auth/WithAuthWrapperComponent";
import {AppFrame} from "@/app/AppFrame";
import {AppPaths} from "@/app/AppPaths";
import {OperatorViewI18N} from "@/translations";
import {SectionedTable} from "@/pages/views/SectionedTable";
import {FullscreenTableDialog} from "@/pages/views/FullscreenTableDialog";
import {RequestOverviewTable} from "@/pages/request-overview-page/RequestOverviewTable";
import {OrderTrackingTable} from "@/pages/order-tracking-page/OrderTrackingTable";
import {useRequestOverviewState} from "@/common/overview-state/useRequestOverviewState";
import {useOrderOverviewState} from "@/common/overview-state/useOrderOverviewState";

/**
 * Innendienstansicht: read-only, platform-wide operator dashboard combining the plain requests
 * overview with a neutral, platform-wide cooperation-orders list (both requester and supplier
 * company names visible). Row click navigates to the request detail page with the order banner.
 *
 * Both request and order halves are fully wired to the shared filter bar, sortable column
 * headers and pagination (#1607) via `requestState`/`orderState`. The Innendienst filters the
 * requesting company through the "Anfragesteller" column header's Enthält-Suche (#1604), the same
 * field the Angebotsgebersicht already used, no page-specific wiring left here.
 */
const OperatorViewPage = () => {
    const { t } = useTranslation();

    // Request filter and rows — account-scoped view state (#1606); the request half is fully wired
    // to the shared filter bar, sortable column headers and pagination (#1607) via
    // `requestState`.
    const requestState = useRequestOverviewState(RequestFilter.INTERNAL);
    const [requestFullscreen, setRequestFullscreen] = useState(false);

    // Order filter and rows — account-scoped view state (#1606). `NEUTRAL` is the platform-wide,
    // operator-only order view; the order half is fully wired to the shared filter bar,
    // sortable column headers and pagination (#1607) via `orderState`.
    const orderState = useOrderOverviewState(OrderFilter.NEUTRAL);
    const [orderFullscreen, setOrderFullscreen] = useState(false);

    // `requestState` carries a stable identity across renders that change none of its fields
    // (memoized in `useOverviewTableState`/`useRequestOverviewState`), so a single dependency is
    // enough here — same sharing rationale as `orderTableNode` below: one reference used by both
    // `SectionedTable` and `FullscreenTableDialog`.
    const requestTableNode = useMemo(
        () => (
            <RequestOverviewTable
                type={RequestFilter.INTERNAL}
                target={AppPaths.INTERNAL_REQUEST_fn}
                state={requestState}
                fillHeight
            />
        ),
        [requestState]
    );

    // `orderState` carries a stable identity across renders that change none of its fields (memoized in
    // `useOverviewTableState`/`useOrderOverviewState`), so a single dependency is enough here — same
    // sharing rationale as `requestTableNode` above: one reference used by both `SectionedTable` and
    // `FullscreenTableDialog`.
    const orderTableNode = useMemo(
        () => <OrderTrackingTable type={OrderFilter.NEUTRAL} state={orderState} fillHeight />,
        [orderState]
    );

    return (
        <AppFrame loading={false}>
            <Box display="flex" flexDirection="column" height="calc(100vh - 120px)" gap={2}>
                {/* Requests section — top 50% */}
                <Box flex={1} minHeight={240} overflow="hidden">
                    <SectionedTable
                        title={t(OperatorViewI18N.page.requestsSectionTitle)}
                        fullscreenTooltip={t(OperatorViewI18N.tooltip.fullscreen)}
                        tableNode={requestTableNode}
                        onFullscreen={() => setRequestFullscreen(true)}
                    />
                </Box>

                {/* Cooperation orders section — bottom 50% */}
                <Box flex={1} minHeight={240} overflow="hidden">
                    <SectionedTable
                        title={t(OperatorViewI18N.page.ordersSectionTitle)}
                        fullscreenTooltip={t(OperatorViewI18N.tooltip.fullscreen)}
                        tableNode={orderTableNode}
                        onFullscreen={() => setOrderFullscreen(true)}
                    />
                </Box>
            </Box>

            {/* Fullscreen dialog for requests */}
            <FullscreenTableDialog
                open={requestFullscreen}
                onClose={() => setRequestFullscreen(false)}
                title={t(OperatorViewI18N.page.requestsSectionTitle)}
                closeLabel={t(OperatorViewI18N.action.closeFullscreen)}
                tableNode={requestTableNode}
            />

            {/* Fullscreen dialog for cooperation orders */}
            <FullscreenTableDialog
                open={orderFullscreen}
                onClose={() => setOrderFullscreen(false)}
                title={t(OperatorViewI18N.page.ordersSectionTitle)}
                closeLabel={t(OperatorViewI18N.action.closeFullscreen)}
                tableNode={orderTableNode}
            />
        </AppFrame>
    );
};

export default withAuthWrapperComponent(OperatorViewPage);
